import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { Form } from "react-bootstrap";
import { FaCheck, FaRegClock, FaMinus, FaPlus } from "react-icons/fa";
import { format } from "date-fns";
import toast from "react-hot-toast";
import axios from "axios";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import PlanSelector from "../../PlanSelector/PlanSelector";
import { PRODUCT_DATA } from "../../../utils/ProductMapper/MapProduct";
import {
  planByPayload,
  toPlanPayload,
  money,
  savingVsMonthly,
  DEFAULT_PLAN,
  isCustomPlan,
} from "../../PlanSelector/plans";
import { slaCopy, slaTarget, formatSlaTime, SLA_LABEL } from "../../../config/sla";
// Shared with the Request a Quote tab so the two entry points offer the same
// platforms and the same seat ceiling.
import { PRODUCT_ORDER, MAX_LICENSES } from "../../QuoteForm/quoteOptions";


const PageWrapper = styled.div`
  min-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const MainCard = styled.div`
  background: #ffffff;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  display: flex;
  max-width: 1000px;
  width: 100%;
  min-height: 550px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const LeftPane = styled.div`
  width: 260px;
  flex-shrink: 0;
  padding: 40px 32px;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 100%;
    padding: 28px 24px 8px;
  }
`;

const RightPane = styled.div`
  flex: 1;
  padding: 40px 48px;
  background-color: #fcfdfe;
  border-left: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 24px;
    border-left: none;
    border-top: 1px solid #f0f0f0;
  }
`;

// Demoted: the left column is a progress tracker, not a second headline. Only
// one thing on this card should read as the task.
const PaneLabel = styled.p`
  color: #64748b;
  font-size: 0.95rem;
  font-weight: 500;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  color: #0f172a;
  font-weight: 700;
  font-size: 1.6rem;
  margin-bottom: 4px;
`;

const Subtitle = styled.p`
  color: #64748b;
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 24px;
`;

const StepItem = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
`;

const StepBadge = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background-color: ${(p) => (p.$done ? "#dcfce7" : p.$active ? "#e0e7ff" : "#f1f5f9")};
  color: ${(p) => (p.$done ? "#166534" : p.$active ? "#4f46e5" : "#94a3b8")};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
  flex-shrink: 0;
  margin-top: 1px;
`;

const StepTitle = styled.h4`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${(p) => (p.$muted ? "#94a3b8" : "#0f172a")};
  margin-bottom: 2px;
`;

const StepDesc = styled.p`
  font-size: 0.85rem;
  color: ${(p) => (p.$active ? "#4f46e5" : "#94a3b8")};
  margin-bottom: 0;
  line-height: 1.4;
`;

const FormLabel = styled(Form.Label)`
  font-size: 0.75rem;
  font-weight: 700;
  color: #1e293b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const StyledFormControl = styled(Form.Control)`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 0.95rem;
  background-color: #f8fafc;
  width: 100%;

  &:focus {
    background-color: #ffffff;
    border-color: #cbd5e1;
    box-shadow: none;
  }
`;

const SubmitButton = styled.button`
  background-color: #3b6fd4;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 16px;
  font-weight: 600;
  font-size: 1.05rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  gap: 8px;
  width: 100%;
  transition: background-color 0.15s;

  &:hover:not(:disabled) {
    background-color: #2f5cb4;
    color: white;
  }

  &:disabled {
    opacity: 0.55;
  }
`;

// Text, not filled — a cancel that looks like a button competes with the
// primary action for the same glance.
const TextButton = styled.button`
  background: none;
  border: none;
  color: #64748b;
  font-size: 0.9rem;
  font-weight: 500;
  padding: 8px 4px;
  text-decoration: underline;

  &:hover {
    color: #0f172a;
  }
`;

const OutlineButton = styled.button`
  flex: 1;
  background-color: #ffffff;
  color: #0f172a;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  font-weight: 500;
  font-size: 0.95rem;

  &:hover {
    background-color: #f8fafc;
    border-color: #cbd5e1;
  }
`;

const PlatformGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 20px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FieldError = styled.p`
  color: #b91c1c;
  font-size: 0.85rem;
  margin: -12px 0 16px;
`;

const PlatformTile = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 8px;
  transition: border-color 0.15s, box-shadow 0.15s;

  img {
    height: 26px;
    max-width: 100%;
    object-fit: contain;
  }

  span {
    font-size: 0.85rem;
    font-weight: 500;
    color: #334155;
    line-height: 1.2;
  }

  &:hover { border-color: #cbd5e1; }
  &:focus-visible { outline: 2px solid #3b82f6; outline-offset: 2px; }

  &[aria-pressed="true"] {
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px #3b82f6;
  }
`;

// The whole grid reddens rather than any one tile — nothing is wrong with a
// particular platform, the choice is simply missing.
const PlatformGridWrap = styled.div`
  ${(p) =>
    p.$invalid &&
    `
    ${PlatformGrid} > button { border-color: #fca5a5; }
  `}
`;

const OrderCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 18px 20px;
  margin-bottom: 16px;
`;

const OrderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
`;

const SummaryBox = styled.div`
  background: #f8fafc;
  border-radius: 14px;
  padding: 18px 20px;
  margin: 16px 0;
`;

const Amount = styled.div`
  color: #0f172a;
  font-size: 1.6rem;
  font-weight: 700;
  white-space: nowrap;
`;

const TrustRow = styled.p`
  text-align: center;
  color: #94a3b8;
  font-size: 0.85rem;
  margin: 10px 0 0;
`;

const QuietLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #64748b;
  font-size: 0.875rem;
  text-decoration: underline;

  &:hover {
    color: #0f172a;
  }
`;

const Stepper = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;

  button {
    background: #ffffff;
    border: none;
    width: 42px;
    height: 42px;
    color: #475569;

    &:hover:not(:disabled) { background: #f1f5f9; }
    &:disabled { color: #cbd5e1; }
  }

  span {
    min-width: 48px;
    text-align: center;
    font-weight: 600;
    color: #0f172a;
  }
`;

const Note = styled.p`
  color: #64748b;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
`;

const StateIcon = styled.span`
  color: ${(p) => p.$color || "#3b6fd4"};
  font-size: 1.15rem;
  margin-right: 10px;
  vertical-align: -2px;
`;

// ── helpers ──────────────────────────────────────────────────────────────────

const productLabel = (code) =>
  (PRODUCT_DATA[code] ? `SPCAD for ${PRODUCT_DATA[code].shortName}` : code) || "—";

// Derived from the row id, which is all the model carries — no reference column
// exists and adding one is a backend change.
const requestReference = (quote) => {
  if (!quote?.id) return null;
  const year = quote.created_at ? format(new Date(quote.created_at), "yyyy") : "";
  return `REQ-${year ? `${year}-` : ""}${String(quote.id).padStart(4, "0")}`;
};

/**
 * The per-license amount for an EXISTING quote.
 *
 * Prefers the config, but falls back to reading the figure out of the stored
 * string. Historical rows carry wording the config no longer produces (one says
 * `save $50`), and a customer looking at their own quote must see the number
 * they were quoted — not a silently re-derived one, and not nothing.
 */
const storedUnitPrice = (planString) => {
  const plan = planByPayload(planString);
  if (plan) return plan.priceUsd;
  const match = /\$\s?([\d,]+(?:\.\d{2})?)/.exec(planString || "");
  return match ? Number(match[1].replace(/,/g, "")) : null;
};

const planDisplayLabel = (planString) =>
  planByPayload(planString)?.label || planString || "—";

const timeOfDay = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : format(d, "h:mm a");
};

const supportHref =
  process.env.REACT_APP_WHATSAPP_URL || "mailto:support@cadomation.com";
const supportLabel = process.env.REACT_APP_WHATSAPP_URL
  ? "Message us on WhatsApp"
  : "Email support";

// ── left column ──────────────────────────────────────────────────────────────

// Static in every state previously, including after activation where it still
// promised a sales callback. Now each step reflects where the request actually is.
function Tracker({ stage, quote }) {
  const submittedAt = timeOfDay(quote?.created_at);

  const steps = [
    {
      title: "Choose a plan",
      state: stage === "picker" ? "active" : "done",
      desc:
        stage === "picker"
          ? "Takes under a minute"
          : submittedAt
            ? `Done ${submittedAt}`
            : "Done",
    },
    {
      title: "Get your link",
      state: stage === "picker" ? "idle" : stage === "pending" ? "active" : "done",
      desc:
        stage === "picker"
          ? "Emailed"
          : stage === "pending"
            ? "In progress"
            : "Sent",
    },
    {
      title: "Start using SPCAD",
      state: stage === "done" ? "done" : stage === "link" ? "active" : "idle",
      desc:
        stage === "done"
          ? "License active"
          : stage === "link"
            ? "One step away"
            : "License Activated",
    },
  ];

  return (
    <>
      <PaneLabel>Get Your license</PaneLabel>
      {steps.map((step, i) => (
        <StepItem key={step.title}>
          <StepBadge $done={step.state === "done"} $active={step.state === "active"}>
            {step.state === "done" ? <FaCheck size={11} /> : i + 1}
          </StepBadge>
          <div>
            <StepTitle $muted={step.state === "idle"}>{step.title}</StepTitle>
            <StepDesc $active={step.state === "active"}>{step.desc}</StepDesc>
          </div>
        </StepItem>
      ))}
    </>
  );
}

// Shared order recap for the two post-submission states.
function OrderRecap({ quote }) {
  const unit = storedUnitPrice(quote.plan);
  const licenses = Number(quote.no_of_licenses) || 1;
  const reference = requestReference(quote);

  return (
    <OrderCard>
      <OrderRow>
        <div>
          <div style={{ color: "#0f172a", fontWeight: 700, fontSize: "1.05rem" }}>
            {productLabel(quote.product)} · {planDisplayLabel(quote.plan)}
          </div>
          <Note style={{ color: "#94a3b8" }}>
            {licenses} {licenses === 1 ? "license" : "licenses"}
          </Note>
        </div>
        {unit !== null && <Amount>{money(unit * licenses)}</Amount>}
      </OrderRow>
      {reference && (
        <Note style={{ color: "#94a3b8", marginTop: 12, fontFamily: "monospace" }}>
          {reference}
        </Note>
      )}
    </OrderCard>
  );
}

export default function RequestQuote() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [quote, setQuote] = useState(null);
  const [platformError, setPlatformError] = useState(false);
  const [messageError, setMessageError] = useState(false);
  // What to restore if the user picks custom and then changes their mind.
  const lastPricedPlan = useRef(toPlanPayload(DEFAULT_PLAN));
  const messageRef = useRef(null);
  const platformRef = useRef(null);

  const [form, setForm] = useState({
    product: "",
    plan: toPlanPayload(DEFAULT_PLAN),
    licenses: 1,
    message: "",
  });

  const fetchQuote = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/quote/me`, {
        headers: { "access-token": token },
      });
      setQuote(response.data?.status ? response.data.quote || null : null);
    } catch (err) {
      // Non-fatal — the panel falls back to the picker.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuote();
  }, []);

  // A cancelled request is a dead end, so the panel reopens the picker rather
  // than stranding the user on it.
  const stage =
    !quote || quote.status === "cancelled"
      ? "picker"
      : quote.status === "payment_link_sent"
        ? "link"
        : quote.status === "completed"
          ? "done"
          : "pending";

  const custom = isCustomPlan(form.plan);
  const plan = planByPayload(form.plan);
  const licenses = form.licenses;
  const total = plan ? plan.priceUsd * licenses : 0;
  const savings = plan ? savingVsMonthly(plan) * licenses : 0;

  const setLicenses = (next) =>
    setForm((prev) => ({ ...prev, licenses: Math.min(MAX_LICENSES, Math.max(1, next)) }));

  // Choosing custom swaps the whole footer into "describe it and send"; picking
  // any priced plan puts it straight back, so leaving is a single click.
  const choosePlan = (next) => {
    if (!isCustomPlan(next)) lastPricedPlan.current = next;
    setMessageError(false);
    setForm((prev) => ({ ...prev, plan: next }));
    if (isCustomPlan(next)) setTimeout(() => messageRef.current?.focus(), 0);
  };

  const leaveCustom = () => {
    setMessageError(false);
    setForm((prev) => ({ ...prev, plan: lastPricedPlan.current }));
  };

  const submit = async (e) => {
    e.preventDefault();
    // Surfaced on the fields themselves. A toast names a problem somewhere off
    // to the side; the red grid points at the thing that needs a click.
    const missingPlatform = !form.product;
    const missingMessage = custom && !form.message.trim();
    setPlatformError(missingPlatform);
    setMessageError(missingMessage);

    if (missingPlatform) {
      // Optional call: not every environment implements scrollIntoView.
      platformRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      return;
    }
    if (missingMessage) {
      messageRef.current?.focus();
      return;
    }
    if (!plan && !custom) return;

    setSubmitting(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/quote/create`, {
        email: localStorage.getItem("email"),
        product: form.product,
        plan: form.plan,
        no_of_licenses: licenses,
        message: form.message,
        // name / phone / country / organisation are omitted on purpose —
        // createQuote() backfills them from the Users row, and sending "" here
        // would overwrite good account data with empty strings.
      });

      if (response.data.status === 1 || response.data.status === -1) {
        toast.success("Request sent — your invoice is on its way.");
        setLoading(true);
        await fetchQuote();
      } else {
        toast.error(response.data.message || "Could not send the request.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // There is no update endpoint, so changing a plan means sending a fresh
  // request; the panel then shows the newest. Spelled out in the dialog because
  // it leaves the earlier request visible to the team.
  const changePlan = () => {
    confirmAlert({
      title: "Change your plan?",
      message:
        "This sends a new request with your new choice. Your earlier request stays with our team until they pick up the newer one.",
      buttons: [
        {
          label: "Choose a new plan",
          onClick: () => {
            setForm((prev) => ({
              ...prev,
              product: quote?.product || prev.product,
              plan: planByPayload(quote?.plan) ? quote.plan : toPlanPayload(DEFAULT_PLAN),
              licenses: Number(quote?.no_of_licenses) || 1,
            }));
            setQuote(null);
          },
        },
        { label: "Keep it" },
      ],
    });
  };

  // No endpoint cancels a quote — /create and /extend are the only user-facing
  // writes, and status changes are admin-only. So this opens a prefilled mail
  // rather than pretending the request is gone; the panel keeps showing
  // "preparing" until the team actions it, which is the truth.
  const cancelRequest = () => {
    const reference = requestReference(quote);
    const subject = `Cancel my request${reference ? ` ${reference}` : ""}`;
    const body = `Please cancel my SPCAD request${reference ? ` (${reference})` : ""}.`;
    window.location.href =
      `mailto:support@cadomation.com?subject=${encodeURIComponent(subject)}` +
      `&body=${encodeURIComponent(body)}`;
    toast("We've opened an email — send it and we'll cancel this for you.");
  };

  const renderPicker = () => (
    <>
      <Title>Set up your license</Title>
      <Subtitle>Prices are fixed — the quote is just how we invoice you.</Subtitle>

      <Form onSubmit={submit}>
        <FormLabel style={platformError ? { color: "#b91c1c" } : undefined}>
          Choose your CAD platform
        </FormLabel>
        <PlatformGridWrap ref={platformRef} $invalid={platformError}>
          <PlatformGrid>
            {PRODUCT_ORDER.map((code) => {
              const product = PRODUCT_DATA[code];
              if (!product) return null;
              return (
                <PlatformTile
                  type="button"
                  key={code}
                  aria-pressed={form.product === code}
                  onClick={() => {
                    setPlatformError(false);
                    setForm((prev) => ({ ...prev, product: code }));
                  }}
                >
                  <img src={product.logo} alt="" />
                  <span>{product.shortName}</span>
                </PlatformTile>
              );
            })}
          </PlatformGrid>
        </PlatformGridWrap>
        {platformError && (
          <FieldError role="alert">Choose your CAD platform to continue.</FieldError>
        )}

        <PlanSelector
          mode="new"
          allowCustom
          value={form.plan}
          onChange={choosePlan}
        />

        <div className="d-flex align-items-center justify-content-between mt-3">
          <FormLabel className="mb-0">Licenses</FormLabel>
          <Stepper>
            <button type="button" onClick={() => setLicenses(licenses - 1)} disabled={licenses <= 1} aria-label="Fewer licenses">
              <FaMinus size={11} />
            </button>
            <span>{licenses}</span>
            <button type="button" onClick={() => setLicenses(licenses + 1)} disabled={licenses >= MAX_LICENSES} aria-label="More licenses">
              <FaPlus size={11} />
            </button>
          </Stepper>
        </div>

        {custom ? (
          <>
            {/* No amount anywhere in this branch: custom pricing has no price
                until someone quotes it, and showing a number we cannot stand
                behind is worse than showing none. */}
            <StyledFormControl
              as="textarea"
              rows={4}
              ref={messageRef}
              className="mt-3"
              placeholder="Tell us what you need — how many licenses, which term, any invoicing or procurement requirements."
              value={form.message}
              onChange={(e) => {
                // Read before the updater runs: React 17 pools synthetic
                // events, so e.target inside a deferred callback is not safe.
                const value = e.target.value;
                setMessageError(false);
                setForm((prev) => ({ ...prev, message: value }));
              }}
              style={messageError ? { borderColor: "#fca5a5" } : undefined}
            />
            {messageError && (
              <FieldError role="alert" style={{ margin: "6px 0 12px" }}>
                Tell us what you need so we can quote it.
              </FieldError>
            )}

            <SummaryBox>
              <div style={{ color: "#0f172a", fontWeight: 600 }}>
                Custom quote · {licenses} {licenses === 1 ? "license" : "licenses"}
              </div>
              <Note>We'll price this and send your invoice.</Note>
            </SummaryBox>

            <SubmitButton type="submit" disabled={submitting}>
              {submitting ? "Sending…" : "Send request"}
            </SubmitButton>

            <TrustRow>
              Nothing charged now · no call required · we'll reply in {SLA_LABEL}
            </TrustRow>

            <div className="text-center mt-2">
              <QuietLink type="button" onClick={leaveCustom}>
                Back to the standard plans
              </QuietLink>
            </div>
          </>
        ) : (
          <>
            <SummaryBox>
              <OrderRow>
                <div>
                  <div style={{ color: "#0f172a", fontWeight: 600 }}>
                    {plan ? plan.label : "—"} · {licenses} {licenses === 1 ? "license" : "licenses"}
                  </div>
                  {savings > 0 && (
                    <Note style={{ color: "#16a34a" }}>
                      Saves {money(savings)} against monthly
                    </Note>
                  )}
                </div>
                <Amount>{money(total)}</Amount>
              </OrderRow>
            </SummaryBox>

            <SubmitButton type="submit" disabled={submitting}>
              {submitting ? "Sending…" : `Send my invoice — ${money(total)}`}
            </SubmitButton>

            <TrustRow>
              Nothing charged now · no call required · invoice in {SLA_LABEL}
            </TrustRow>
          </>
        )}
      </Form>
    </>
  );

  const renderPending = () => {
    const expected = slaCopy(quote.created_at);
    const targetTime = formatSlaTime(slaTarget(quote.created_at));

    return (
      <>
        <Title>
          We're preparing your invoice
        </Title>
        <Subtitle>
          Expected in your inbox. We'll email you and it'll show up right here as well.
        </Subtitle>

        <OrderRecap quote={quote} />

        {/* <div className="d-flex gap-2 align-items-center mb-3">
          <OutlineButton type="button" onClick={changePlan}>
            Change plan
          </OutlineButton>
          <TextButton type="button" onClick={cancelRequest}>
            Cancel request
          </TextButton>
        </div> */}

        <Note style={{ color: "#94a3b8" }}>
          Nothing yet?{" "}
          <a href={supportHref + "&text=%20I%20need%20assitance!%20invoice%20not%20received%20yet."} target="_blank" rel="noopener noreferrer" style={{ color: "#3b6fd4" }}>
            {supportLabel}
          </a>{" "}
          and we'll send it straight over.
        </Note>
      </>
    );
  };

  const renderLinkReady = () => {
    const unit = storedUnitPrice(quote.plan);
    const licenseCount = Number(quote.no_of_licenses) || 1;
    const amount = unit !== null ? money(unit * licenseCount) : null;

    return (
      <>
        <Title>Your payment link is ready</Title>
        <Subtitle>Pay it and we'll activate your license.</Subtitle>

        <OrderRecap quote={quote} />

        {/* Neutral by design: the provider varies per quote and nothing in the
            model records which one was used, so naming one would be a guess. */}
        <Note className="mb-2" style={{ textAlign: "center" }}>
          This opens our payment page.
        </Note>

        <SubmitButton
          as="a"
          href={quote.payment_link}
          target="_blank"
          rel="noopener noreferrer"
        >
          {amount ? `Pay ${amount} now` : "Open payment page"}
        </SubmitButton>

      </>
    );
  };

  const renderDone = () => (
    <>
      <Title>
        Your license is active
      </Title>
      <Subtitle>
        Payment received. Your license key and invoice are in your email
        {localStorage.getItem("email") ? ` at ${localStorage.getItem("email")}` : ""}.
      </Subtitle>

      <OrderRecap quote={quote} />

      <div className="d-flex gap-2 mb-3">
        <OutlineButton type="button" onClick={() => window.location.assign("/User/licenses")}>
          View my licenses
        </OutlineButton>
      </div>

      <SummaryBox>
        <div style={{ color: "#0f172a", fontWeight: 600, marginBottom: 4 }}>
          Know someone who'd use SPCAD?
        </div>
        <Note>
          Send them our way — mention them when you next contact us and we'll look
          after you both.
          {/* <a href="mailto:?subject=SPCAD&body=https://cadomation.com" style={{ color: "#3b6fd4" }}>
            Share SPCAD
          </a> */}
        </Note>
      </SummaryBox>

      <Note style={{ color: "#94a3b8" }}>
        Need a hand setting up?{" "}
        <a href="https://help.cadomation.com/spcad/docs/spcadhelp/" target="_blank" rel="noopener noreferrer" style={{ color: "#64748b" }}>
          Help docs
        </a>
      </Note>
    </>
  );

  return (
    <PageWrapper>
      <MainCard>
        <LeftPane>
          <Tracker stage={stage} quote={quote} />
        </LeftPane>

        <RightPane>
          {loading ? (
            <Note>Loading…</Note>
          ) : stage === "picker" ? (
            renderPicker()
          ) : stage === "pending" ? (
            renderPending()
          ) : stage === "link" ? (
            renderLinkReady()
          ) : (
            renderDone()
          )}
        </RightPane>
      </MainCard>
    </PageWrapper>
  );
}
